import { useCallback, useState } from 'react';

import useAuth from 'hooks/authentication/useAuth';
import { PRIORITY } from 'hooks/permission/constants';
import type { ProjectNames, RoleNames } from 'hooks/permission/types';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import { useAppContext } from 'store/AppProvider';

type GroupsState = Record<
    string,
    {
        role: RoleNames;
    }
>;

const useInitPermission = () => {
    const { getPermission } = useAuth();
    const { appState, setPermissionPriority, setPermissionAvailableRoles, setPermissionAvailableProjects, setPermissionGroups } =
        useAppContext();

    const [isLoading, setLoading] = useState(true);

    const getPriorityGroup = (
        groupsObject: GroupsState
    ): {
        rolePriority: RoleNames;
        projectPriority: string;
    } => {
        let rolePriorityIndex = 10;
        let priorityGroup: [string, { role: RoleNames }] | [] = [];
        Object.entries(groupsObject).forEach((group) => {
            const roleIndex = PRIORITY.indexOf(group[1].role);

            if (roleIndex <= rolePriorityIndex) {
                rolePriorityIndex = roleIndex;
                priorityGroup = group;
            }
        });
        const priorityObject = {
            rolePriority: PRIORITY[rolePriorityIndex],
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            projectPriority: priorityGroup.length ? priorityGroup[0] : '',
        };

        setPermissionPriority(priorityObject);

        return priorityObject;
    };

    const formatGroup = useCallback(async () => {
        const awsPermissionArray: string[] = await getPermission();

        let groupObject = {};
        const projectNamesList: ProjectNames[] = [];
        const availableRolesList: RoleNames[] = [];

        const projectWithRole = awsPermissionArray.filter((element) => element.includes(':'));

        projectWithRole.forEach((group) => {
            const groupSplit = group.split(':');

            const roleName = groupSplit[1] as RoleNames | undefined;
            const projectName = groupSplit[0] as unknown as ProjectNames | '';

            groupObject = {
                ...groupObject,
                [projectName]: {
                    role: roleName,
                },
            };
            if (projectName) {
                projectNamesList.push(projectName);
            }
            if (roleName) {
                availableRolesList.push(roleName);
            }
        });
        const dedupedRoles = Array.from(new Set(availableRolesList));

        // Guard: never overwrite a populated roles list with an empty one.
        // initPermission can run on token refresh; a transient failure
        // (network blip, missing cognito:groups claim) would otherwise flip
        // routeType from 'main' to 'no-permission' and remount the entire app.
        const hadRolesBefore = appState.generalData.permissionAvailableRoles.length > 0;
        if (dedupedRoles.length === 0 && hadRolesBefore) {
            logger.warn('initPermission returned empty roles after a populated state — skipping dispatch', {
                tags: {
                    reason: ERROR_REASONS.emptyRolesDispatch,
                    awsPermissionLen: String(awsPermissionArray.length),
                    url: window.location.pathname,
                },
            });
            setLoading(false);
            return;
        }

        // React 18 automatically batches all state updates, no matter where they're queued.
        setPermissionAvailableRoles(dedupedRoles);
        setPermissionAvailableProjects(Array.from(new Set(projectNamesList)));
        setPermissionGroups(groupObject);

        getPriorityGroup(groupObject);
        setLoading(false);
    }, [appState.generalData.permissionAvailableRoles.length]);

    const init = async () => {
        await formatGroup();
    };

    return {
        isLoading: isLoading,
        initPermission: init,
    };
};

export default useInitPermission;
